import React from "react";
import {
  BadgeCheck,
  Check,
  CircleDot,
  ClipboardList,
  FileText,
  Minus,
  ShieldCheck,
  ShieldPlus,
  ShoppingBag,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { Badge } from "../../Franchise/D2CDashboard/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../Franchise/D2CDashboard/ui/card";
import { cn } from "../../Franchise/D2CDashboard/ui/utils";
import { formatDate, formatDateTime } from "../common/format";
import { STATE_LABEL, detailRows } from "./journeyStages";

const STAGE_ICON = {
  sold: ShoppingBag,
  registered: ClipboardList,
  invoice: FileText,
  verification: ShieldCheck,
  active: BadgeCheck,
  plan: ShieldPlus,
  services: Wrench,
  referral: Users,
};

// The dot of each state: done is filled, current is ringed, failed is red, skipped is dashed.
const DOT = {
  done: "tw-border-primary tw-bg-primary tw-text-primary-foreground",
  current: "tw-border-primary tw-bg-accent tw-text-primary tw-ring-4 tw-ring-primary/15",
  pending: "tw-border-border tw-bg-background tw-text-muted-foreground",
  failed: "tw-border-red-500 tw-bg-red-50 tw-text-red-700 tw-ring-4 tw-ring-red-100",
  skipped: "tw-border-dashed tw-border-slate-300 tw-bg-slate-50 tw-text-slate-400",
};
const STATE_TONE = { done: "info", current: "info", pending: "neutral", failed: "danger", skipped: "neutral" };

const StateIcon = ({ stage }) => {
  if (stage.state === "done") return <Check className="tw-size-4" aria-hidden="true" />;
  if (stage.state === "failed") return <X className="tw-size-4" aria-hidden="true" />;
  if (stage.state === "skipped") return <Minus className="tw-size-4" aria-hidden="true" />;
  const Icon = STAGE_ICON[stage.key] || CircleDot;
  return <Icon className="tw-size-4" aria-hidden="true" />;
};

// The line between two stages is solid once the stage before it is done.
const Connector = ({ done }) => (
  <span
    aria-hidden="true"
    className={cn(
      "tw-absolute tw-left-[calc(50%+18px)] tw-right-[calc(-50%+18px)] tw-top-[17px] tw-h-0.5 tw-rounded-full",
      done ? "tw-bg-primary/60" : "tw-bg-slate-200",
    )}
  />
);

// The details of the open stage: its summary and its `details` as label and value pairs.
export const StageDetails = ({ stage }) => {
  const rows = detailRows(stage.details);
  return (
    <div
      id={`stage-panel-${stage.key}`}
      role="region"
      aria-label={`${stage.label} details`}
      className="tw-rounded-md tw-border tw-bg-muted/40 tw-p-3"
    >
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <strong className="tw-text-sm tw-font-semibold">{stage.label}</strong>
        <Badge tone={STATE_TONE[stage.state]}>{STATE_LABEL[stage.state]}</Badge>
        {stage.at && <span className="tw-text-xs tw-text-muted-foreground">{formatDateTime(stage.at)}</span>}
      </div>
      {stage.summary && <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-text-foreground">{stage.summary}</p>}
      {rows.length > 0 ? (
        <dl className="tw-m-0 tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-2 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
          {rows.map((row) => (
            <div key={row.key} className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
              <dt className="tw-text-xs tw-font-normal tw-text-muted-foreground">{row.label}</dt>
              <dd className="tw-m-0 tw-break-words tw-text-sm tw-text-foreground">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="tw-m-0 tw-mt-1.5 tw-text-xs tw-text-muted-foreground">No more details for this stage.</p>
      )}
    </div>
  );
};

// Sold → Registered → Invoice → Verification → Active → Plan → Services → Referral, in the order the
// service sends them. Each stage is a button that opens its details below the tracker.
const StageTracker = ({ stages, openKey, onToggle }) => {
  const open = stages.find((stage) => stage.key === openKey) || null;
  return (
    <Card>
      <CardHeader className="tw-pb-2">
        <CardTitle as="h2">Journey</CardTitle>
      </CardHeader>
      <CardContent className="tw-space-y-3">
        <div className="tw-overflow-x-auto tw-pb-1">
          <ol
            aria-label="Warranty stages"
            className="tw-m-0 tw-grid tw-min-w-[880px] tw-list-none tw-p-0"
            style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))` }}
          >
            {stages.map((stage, index) => {
              const isOpen = stage.key === openKey;
              return (
                <li key={stage.key} data-state={stage.state} className="tw-relative tw-flex tw-min-w-0 tw-flex-col">
                  {index < stages.length - 1 && <Connector done={stage.state === "done"} />}
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? `stage-panel-${stage.key}` : undefined}
                    aria-label={`${stage.label}: ${STATE_LABEL[stage.state]}`}
                    onClick={() => onToggle(stage.key)}
                    className={cn(
                      "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-items-center tw-gap-1 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-1 tw-pb-1.5 tw-text-center",
                      "hover:tw-bg-accent/50 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring",
                      isOpen && "tw-bg-accent/60",
                    )}
                  >
                    <span
                      className={cn(
                        "tw-relative tw-z-[1] tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-full tw-border-2",
                        DOT[stage.state],
                      )}
                    >
                      <StateIcon stage={stage} />
                    </span>
                    <span
                      className={cn(
                        "tw-text-xs tw-font-medium",
                        stage.state === "pending" || stage.state === "skipped"
                          ? "tw-text-muted-foreground"
                          : stage.state === "failed"
                            ? "tw-text-red-700"
                            : "tw-text-foreground",
                      )}
                    >
                      {stage.label}
                    </span>
                    <span className="tw-text-[11px] tw-leading-4 tw-text-muted-foreground">
                      {stage.at ? formatDate(stage.at) : STATE_LABEL[stage.state]}
                    </span>
                    {stage.summary && (
                      <span
                        className="tw-line-clamp-2 tw-w-full tw-text-[11px] tw-leading-4 tw-text-muted-foreground"
                        title={stage.summary}
                      >
                        {stage.summary}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
        {open && <StageDetails stage={open} />}
      </CardContent>
    </Card>
  );
};

export default StageTracker;
